"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";
import type { TagChipColor } from "./tag-chip";

// CALENDAR-C1 (docs/design/2026-10-28-calendar.md §3): the native calendar's coloured parts. An
// event is a light chip in its calendar's tag colour (the `--tag-{color}` soft fill and its AA
// text, light and dark); a busy block is hatched and muted; a declined event is an outline; a
// task is a quiet sunken chip (overdue tinted). The time grid places a block by minutes from
// midnight (`CALENDAR_HOUR_PX` per hour) in its overlap lane. Position and colour are data, so
// they are inline styles here, never in the app.

/** Pixels per hour of the time grid. */
export const CALENDAR_HOUR_PX = 48;

export type CalendarChipVariant = "event" | "busy" | "declined" | "task" | "overdue";

const HATCH =
  "border border-border bg-[repeating-linear-gradient(135deg,var(--muted)_0_6px,var(--sunken)_6px_12px)] text-muted-foreground";

function variantClass(variant: CalendarChipVariant): string {
  switch (variant) {
    case "busy":
      return HATCH;
    case "declined":
      return "border border-dashed border-border-strong bg-transparent text-muted-foreground line-through decoration-1";
    case "task":
      return "border border-subtle bg-sunken text-secondary";
    case "overdue":
      return "border border-subtle bg-danger-soft text-danger-text";
    default:
      return "";
  }
}

function variantStyle(color: TagChipColor, variant: CalendarChipVariant): React.CSSProperties {
  if (variant !== "event") return {};
  return {
    backgroundColor: `var(--tag-${color})`,
    color: `var(--tag-${color}-foreground)`,
    borderColor: `var(--tag-${color}-foreground)`,
  };
}

type ChipProps = useRender.ComponentProps<"button"> & {
  color?: TagChipColor;
  variant?: CalendarChipVariant;
};

/** A one-line chip (all-day row, month cell, task overlay); a `button` unless `render` says. */
export function CalendarChip({
  color = "zinc",
  variant = "event",
  className,
  render,
  ...props
}: ChipProps) {
  return useRender({
    defaultTagName: "button",
    render,
    props: mergeProps<"button">(
      {
        className: cn(
          "flex h-[22px] w-full min-w-0 items-center gap-1.5 rounded-sm px-1.5 text-left text-2xs font-ui whitespace-nowrap transition-[filter] duration-(--dur-fast) hover:brightness-[0.97]",
          variant === "event" && "border-l-2",
          variantClass(variant),
          FOCUS_RING,
          className,
        ),
        style: variantStyle(color, variant),
        ...(render ? {} : { type: "button" as const }),
      },
      props,
    ),
    state: { slot: "calendar-chip" },
  });
}

/** A time-grid block, placed by minutes from local midnight in its overlap lane. */
export function CalendarBlock({
  top,
  height,
  lane = 0,
  lanes = 1,
  color = "zinc",
  variant = "event",
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"div">, "style"> & {
  top: number;
  height: number;
  lane?: number;
  lanes?: number;
  color?: TagChipColor;
  variant?: CalendarChipVariant;
}) {
  const px = CALENDAR_HOUR_PX / 60;
  const width = 100 / Math.max(1, lanes);
  return (
    <div
      data-slot="calendar-block"
      data-variant={variant}
      className={cn(
        "group/row absolute overflow-hidden rounded-md text-2xs font-ui",
        variant === "event" && "border-l-2",
        variantClass(variant),
        className,
      )}
      style={{
        ...variantStyle(color, variant),
        top: `${top * px}px`,
        height: `${Math.max(18, height * px - 2)}px`,
        left: `calc(${lane * width}% + 2px)`,
        width: `calc(${width}% - 4px)`,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

/** A calendar's colour dot (sidebar rows, the detail modal, the colour picker). */
export function CalendarDot({
  color = "zinc",
  className,
  ...props
}: Omit<React.ComponentProps<"span">, "style" | "color"> & { color?: TagChipColor }) {
  return (
    <span
      aria-hidden="true"
      data-slot="calendar-dot"
      className={cn("inline-block size-2.5 shrink-0 rounded-full", className)}
      style={{ backgroundColor: `var(--tag-${color}-foreground)` }}
      {...props}
    />
  );
}

/** The "now" line across today's column, `minutes` after local midnight. */
export function CalendarNowLine({ minutes, className }: { minutes: number; className?: string }) {
  return (
    <div
      aria-hidden="true"
      data-slot="calendar-now-line"
      className={cn(
        "pointer-events-none absolute inset-x-0 z-10 h-px bg-danger-text before:absolute before:-top-[3px] before:-left-[3px] before:size-[7px] before:rounded-full before:bg-danger-text",
        className,
      )}
      style={{ top: `${(minutes * CALENDAR_HOUR_PX) / 60}px` }}
    />
  );
}

/**
 * The time grid's day column: `hours` tall at `CALENDAR_HOUR_PX` (24 by default; fewer when the
 * hours outside working hours are folded away).
 */
export function CalendarDayColumn({
  hours = 24,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "style"> & { hours?: number }) {
  return (
    <div
      data-slot="calendar-day-column"
      className={cn("relative", className)}
      style={{ height: `${hours * CALENDAR_HOUR_PX}px` }}
      {...props}
    />
  );
}
